/**
 * Cross-iframe handshake so child surfaces can inherit observability creds
 * from their top-level host.
 *
 * Hosts call `installHostListener(buildReply)` to answer queries; children
 * call `queryHost()` to fetch the host's reply (or null on timeout / when
 * not in an iframe).
 *
 * Note: `apps/registration-app/server/views/observability.js` is an SSR
 * template injected into reg-app's HTML and cannot import from the
 * workspace, so it hand-writes the same two string constants and answers
 * queries without `installHostListener`. If you change them here, update
 * that file too.
 */

export const HOST_QUERY_TYPE = 'gc:observability-host-query';
export const HOST_REPLY_TYPE = 'gc:observability-host-reply';

export interface LogRocketHostReply {
  type: typeof HOST_REPLY_TYPE;
  tool: 'logrocket';
  host: 'admin-app';
  logrocket: {
    appID: string;
    parentDomain: string;
    ingestServer?: string;
    sdkServer?: string;
  };
}

export interface DatadogHostReply {
  type: typeof HOST_REPLY_TYPE;
  tool: 'datadog';
  host: 'reg-app';
  datadog: {
    applicationId: string;
    clientToken: string;
    env: string;
    version: string;
    trackingConsent?: 'granted' | 'not-granted';
  };
}

export type HostReply = LogRocketHostReply | DatadogHostReply;

function isHostReply(data: unknown): data is HostReply {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const d = data as Record<string, unknown>;
  if (d.type !== HOST_REPLY_TYPE) {
    return false;
  }
  return (
    (d.tool === 'logrocket' && typeof d.logrocket === 'object' && d.logrocket !== null) ||
    (d.tool === 'datadog' && typeof d.datadog === 'object' && d.datadog !== null)
  );
}

export function installHostListener(buildReply: () => HostReply): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.addEventListener('message', event => {
    if (event.data?.type !== HOST_QUERY_TYPE || !event.source || !event.origin) {
      return;
    }
    (event.source as Window).postMessage(buildReply(), event.origin);
  });
}

function parentOrigin(): string | null {
  try {
    const ref = document.referrer;
    if (!ref) {
      return null;
    }
    return new URL(ref).origin;
  } catch {
    return null;
  }
}

export function queryHost(timeoutMs = 200): Promise<HostReply | null> {
  return new Promise(resolve => {
    if (typeof window === 'undefined' || window.top === window.self) {
      resolve(null);
      return;
    }
    const targetOrigin = parentOrigin();
    if (!targetOrigin) {
      // Without a known parent origin we can't post safely (e.g. when the
      // iframe was opened with no-referrer). Skip the handshake.
      resolve(null);
      return;
    }
    let settled = false;
    const finish = (value: HostReply | null) => {
      if (settled) {
        return;
      }
      settled = true;
      window.removeEventListener('message', onMsg);
      resolve(value);
    };
    const onMsg = (e: MessageEvent) => {
      if (e.source !== window.top || e.origin !== targetOrigin || !isHostReply(e.data)) {
        return;
      }
      finish(e.data);
    };
    window.addEventListener('message', onMsg);
    try {
      window.top?.postMessage({ type: HOST_QUERY_TYPE }, targetOrigin);
    } catch {
      // Sandboxed iframes can throw on top access; fall through to timeout.
    }
    setTimeout(() => finish(null), timeoutMs);
  });
}
